/**
 * EmailThreadPanel - Slideout for viewing a full email thread.
 *
 * Mounted by `SlideoutRenderer` when the top entry of the slideout stack
 * is an `email_thread` kind. Composite entry id format: `${proposalId}-${actionId}`.
 *
 * Built on `SlideoutMenu` from `@/lib/base/overlays`, so animation,
 * backdrop, focus management, dismiss-on-Escape, dismiss-on-outside-click,
 * and z-index stacking all come from the design-system wrapper. Entity
 * badges rendered inside the thread can `pushSlideout({ kind: 'entity', id })`
 * to layer on top — closing them reveals this panel again.
 */

import { ChevronDown, Mail01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Badge } from '@/lib/base/badges/badges';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { QueryErrorState } from '@/lib/components';
import { SlideoutOriginProvider } from '@/lib/crm/contexts/SlideoutOriginContext';
import { type SlideoutEntry } from '@/lib/crm/contexts/SlideoutStackContext';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import { getAvatarInitials, getGravatarUrl } from '@/lib/crm/utils/avatar';
import { extractEmail } from '@/lib/crm/utils/email-address';
import { GmailIcon } from '@/lib/crm/utils/icons/CustomIcons';
import type { ThreadEmailMessage } from '@/lib/proposals/core/types';
import { useEmailThread } from '@/lib/proposals/hooks/useEmailThread';

import { decodeEmailThreadEntryId } from './emailThreadEntryId';
import {
  buildDisplayHtml,
  decodeHtmlEntities,
  extractName,
  formatDate,
  parseEmailBody,
} from './emailThreadPanel.utils';

export interface EmailThreadPanelProps {
  /** Stack entry this slideout renders. Must be `kind: 'email_thread'`. */
  entry: SlideoutEntry;
}

/**
 * Individual email message in the thread
 */
function EmailMessage({
  message,
  isExpanded,
  onToggle,
}: {
  message: ThreadEmailMessage;
  isExpanded: boolean;
  onToggle: (messageId: string, isExpanded: boolean) => void;
}) {
  const [showQuoted, setShowQuoted] = useState(false);
  const displayTimezone = useDisplayTimezone();

  const handleExpandedChange = useCallback(
    (expanded: boolean) => onToggle(message.id, expanded),
    [message.id, onToggle]
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Parse the raw email body once per message change. Independent of
  // showQuoted so toggling it doesn't recompute this.
  const parsedBody = useMemo(
    () => parseEmailBody(message.body_html || message.body),
    [message.body, message.body_html]
  );

  // Only reflects showQuoted while expanded, so toggling it while collapsed
  // doesn't change this value and doesn't re-invoke the iframe-update effect
  // below.
  const effectiveShowQuoted = isExpanded && showQuoted;

  // Update iframe when expanded or showQuoted changes
  useEffect(() => {
    if (isExpanded && iframeRef.current) {
      const html = buildDisplayHtml(
        parsedBody.processedBody,
        effectiveShowQuoted
      );
      const iframe = iframeRef.current;
      const doc = iframe.contentWindow?.document;
      if (doc) {
        // Detect dark mode from the parent document (class-based)
        const isDarkMode =
          document.documentElement.classList.contains('dark') ||
          document.documentElement.getAttribute('data-theme') === 'dark';

        const cs = getComputedStyle(document.documentElement);
        const textPrimary = cs.getPropertyValue('--color-text-primary').trim();
        const bgPrimary = cs.getPropertyValue('--color-bg-primary').trim();
        const brand600 = cs.getPropertyValue('--color-brand-600').trim();
        const brand500 = cs.getPropertyValue('--color-brand-500').trim();
        const borderPrimary = cs
          .getPropertyValue('--color-border-primary')
          .trim();
        const borderSecondary = cs
          .getPropertyValue('--color-border-secondary')
          .trim();
        const textSecondary = cs
          .getPropertyValue('--color-text-secondary')
          .trim();

        // Light mode styles
        const lightStyles = `
          body {
            color: ${textPrimary};
            background-color: ${bgPrimary};
          }
          a { color: ${brand600}; }
          blockquote {
            border-left-color: ${borderPrimary};
            color: ${textSecondary};
          }
        `;

        // Dark mode styles
        const darkStyles = `
          body {
            color: ${textPrimary};
            background-color: ${bgPrimary};
          }
          a { color: ${brand500}; }
          blockquote {
            border-left-color: ${borderSecondary};
            color: ${textSecondary};
          }
        `;

        doc.open();
        doc.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <base target="_blank">
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body {
                  margin: 0;
                  padding: 12px;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  font-size: 14px;
                  line-height: 1.6;
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                }
                * { max-width: 100%; }
                img { max-width: 100%; height: auto; }
                table { max-width: 100%; border-collapse: collapse; }
                a { text-decoration: underline; }
                blockquote {
                  margin: 0;
                  padding-left: 10px;
                  border-left: 2px solid;
                }
                ${isDarkMode ? darkStyles : lightStyles}
              </style>
            </head>
            <body>${html}</body>
          </html>
        `);
        doc.close();

        // Resize iframe to content
        const resizeTimer = setTimeout(() => {
          const height = doc.body?.scrollHeight || 200;
          iframe.style.height = `${height}px`;
        }, 100);
        return () => clearTimeout(resizeTimer);
      }
    }
  }, [isExpanded, effectiveShowQuoted, parsedBody]);

  const handleToggleQuoted = useCallback(
    () => setShowQuoted(prev => !prev),
    []
  );

  const { hasQuoted } = parsedBody;
  const senderName = extractName(message.from);
  const senderEmail = extractEmail(message.from);

  return (
    <AccordionItem
      isExpanded={isExpanded}
      onExpandedChange={handleExpandedChange}
      className={`border rounded-lg overflow-hidden ${
        message.is_trigger
          ? 'border-utility-brand-300 bg-utility-brand-50 dark:bg-utility-brand-900/20'
          : 'border-primary bg-primary'
      }`}
    >
      <AccordionTrigger className='w-full px-4 py-3 flex items-start gap-3 hover:bg-secondary/50 text-left'>
        <Avatar
          size='sm'
          src={senderEmail ? getGravatarUrl(senderEmail, 64) : undefined}
          initials={
            getAvatarInitials(senderName) || senderName.charAt(0).toUpperCase()
          }
          alt={senderName}
          className={
            message.is_trigger
              ? 'bg-utility-brand-200 dark:bg-utility-brand-800'
              : 'bg-secondary'
          }
          contrastBorder={!message.is_trigger}
        />

        <div className='flex-1 min-w-0'>
          <div className='flex items-baseline justify-between gap-2 mb-0.5 min-w-0'>
            <div className='flex items-center gap-2 min-w-0'>
              <span
                className='font-semibold text-primary text-sm truncate'
                title={decodeHtmlEntities(message.from)}
              >
                {decodeHtmlEntities(message.from)}
              </span>
              {message.is_trigger && (
                <Badge
                  type='color'
                  color='brand'
                  size='sm'
                  className='text-[10px] shrink-0'
                >
                  TRIGGER
                </Badge>
              )}
            </div>
            <span className='text-xs text-tertiary shrink-0'>
              {formatDate(message.date, displayTimezone)}
            </span>
          </div>
          <div
            className='text-xs text-secondary truncate'
            title={`to ${decodeHtmlEntities(message.to.join(', '))}${message.cc.length > 0 ? ` · cc: ${message.cc.join(', ')}` : ''}`}
          >
            to {decodeHtmlEntities(message.to.join(', '))}
            {message.cc.length > 0 && ` · cc: ${message.cc.join(', ')}`}
          </div>
          {/* Snippet preview shown only when collapsed (CSS-only via
              group-data-[expanded] hide). */}
          <div className='mt-0.5 text-sm text-secondary line-clamp-2 group-data-[expanded]/item:hidden'>
            {decodeHtmlEntities(message.snippet)}
          </div>
        </div>

        <AccordionChevron className='w-5 h-5 text-tertiary' />
      </AccordionTrigger>

      <AccordionPanel className='px-4 pb-4 border-t border-primary/50'>
        <iframe
          ref={iframeRef}
          title={`Email from ${senderName}`}
          className='w-full border-0 mt-3 min-h-[150px]'
          sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
        />
        {hasQuoted && (
          <button
            type='button'
            onClick={handleToggleQuoted}
            className='mt-3 text-xs text-secondary hover:text-primary font-medium flex items-center gap-1'
          >
            <ChevronDown
              className={`w-3 h-3 transition-transform ${
                showQuoted ? 'rotate-180' : ''
              }`}
              aria-hidden='true'
            />
            {showQuoted ? 'Hide' : 'Show'} quoted text
          </button>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}

/**
 * Loading state for the panel
 */
function LoadingState() {
  return (
    <div className='flex items-center justify-center py-12'>
      <LoadingIndicator size='sm' label='Loading thread…' />
    </div>
  );
}

/**
 * Slideout for viewing a full email thread.
 */
export default function EmailThreadPanel({ entry }: EmailThreadPanelProps) {
  const { currentOrg } = useOrg();
  const { clearToProposalBase } = useSlideoutStack();
  // Share this slideout's resizable width with the rest of the app via
  // SlideoutWidthContext (only the topmost slideout renders, so the context
  // always reflects the visible slideout). The floating composer reads this
  // to compute its right offset — without it the composer would offset by a
  // stale/default width and overlap this panel once it's resized.
  const { width: slideoutWidth, setWidth: setSlideoutWidth } =
    useSlideoutWidth();
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set()
  );

  const ids = useMemo(() => decodeEmailThreadEntryId(entry.id), [entry.id]);
  const proposalId = ids?.proposalId ?? 0;
  const actionId = ids?.actionId ?? 0;
  const orgId = currentOrg?.id ?? 0;

  // Fetch thread data once we have a real org + parsed ids. The decoder
  // returns null for any input that isn't a pair of positive integers, so
  // `ids !== null` is sufficient to confirm both `proposalId` and
  // `actionId` are valid db ids before enabling the query.
  const idsValid = ids !== null && proposalId > 0 && actionId > 0;
  const queryEnabled = idsValid && orgId !== 0;
  const { data, isLoading, isError, error, refetch } = useEmailThread(
    orgId,
    proposalId,
    actionId,
    { enabled: queryEnabled }
  );

  // While the ids are valid but the org hasn't resolved yet (orgId === 0), the
  // query is disabled, so `isLoading` is false and `data` is undefined. Treat
  // that window as loading — otherwise the settled-empty branch below would
  // fire against a query that never ran.
  const isAwaitingOrg = idsValid && orgId === 0;

  // Auto-expand trigger message when data loads
  useEffect(() => {
    if (data?.messages) {
      const triggerMessage = data.messages.find(m => m.is_trigger);
      if (triggerMessage) {
        setExpandedMessages(new Set([triggerMessage.id]));
      }
    }
  }, [data]);

  // Dismissing the panel (X, click-outside, Esc) reveals the proposal it was
  // opened over, or closes everything when no proposal is on the stack (e.g.
  // on the full-page actions route). One-level back-navigation is the entity
  // slideout header's back button; this panel has no back affordance.
  const handleClose = useCallback(() => {
    clearToProposalBase();
  }, [clearToProposalBase]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleToggleMessage = useCallback(
    (messageId: string, isExpanded: boolean) => {
      setExpandedMessages(prev => {
        const next = new Set(prev);
        if (isExpanded) {
          next.add(messageId);
        } else {
          next.delete(messageId);
        }
        return next;
      });
    },
    []
  );

  // Build external link from `data.external_link`, or construct from thread_id.
  const externalLink =
    data?.external_link ||
    (data?.provider !== 'ms365' && data?.thread_id
      ? `https://mail.google.com/mail/u/0/#all/${data.thread_id}`
      : null);

  const isOutlook = data?.provider === 'ms365';
  const openLabel = isOutlook ? 'Open in Outlook' : 'Open in Gmail';

  const header = useMemo(
    () => (
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2 min-w-0'>
          <Mail01 className='size-5 text-tertiary shrink-0' />
          <h2 className='text-lg font-semibold text-primary truncate'>
            Email thread
          </h2>
        </div>
        {externalLink && (
          <a
            href={externalLink}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-secondary hover:text-primary border border-primary rounded-lg hover:bg-secondary transition-colors shrink-0'
          >
            {isOutlook ? (
              <img
                src='/assets/figma/outlook-icon.svg'
                alt=''
                className='size-4'
              />
            ) : (
              <GmailIcon className='size-4' />
            )}
            {openLabel}
          </a>
        )}
      </div>
    ),
    [externalLink, isOutlook, openLabel]
  );

  return (
    <SlideoutMenu
      isOpen={true}
      onOpenChange={handleOpenChange}
      isDismissable
      resizable
      initialWidth={slideoutWidth}
      onWidthChange={setSlideoutWidth}
      showBackdrop={false}
      header={header}
      dialogAriaLabel='Email thread'
    >
      {/* Clicks inside the thread (entity badges on messages) are "from
          within the stack" — useOpenSlideout layers them on top of this
          thread so back returns here. */}
      <SlideoutOriginProvider entry={entry}>
        {!idsValid && (
          <div className='py-12 text-center'>
            <p className='text-sm text-secondary'>
              Email thread reference is invalid.
            </p>
          </div>
        )}

        {(isLoading || isAwaitingOrg) && <LoadingState />}

        {isError && (
          <QueryErrorState
            error={error}
            label="We couldn't load the email thread"
            onRetry={refetch}
          />
        )}

        {data && data.messages && data.messages.length > 0 && (
          <div className='flex flex-col gap-3 py-4'>
            {/* Show newest messages first */}
            {[...data.messages].reverse().map(message => (
              <EmailMessage
                key={message.id}
                message={message}
                isExpanded={expandedMessages.has(message.id)}
                onToggle={handleToggleMessage}
              />
            ))}
          </div>
        )}

        {/* Settled with nothing renderable: either the endpoint served no thread
            for this action (`thread: null`) or the thread carries no messages.
            Without the null arm this surface rendered an entirely blank body —
            no loading, no error, no empty state. Gate on `queryEnabled` so a
            disabled query (org not yet loaded) shows the loading state above,
            not a false "no messages" — `data` is undefined in both the disabled
            and the loading cases, so `!isLoading` alone isn't enough. */}
        {queryEnabled && !isLoading && !isError && !data?.messages?.length && (
          <div className='text-center py-12'>
            <p className='text-secondary'>No messages found in this thread</p>
          </div>
        )}
      </SlideoutOriginProvider>
    </SlideoutMenu>
  );
}
